import { useState, useCallback } from "react";
import { useGetList, useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { X, Plus, Loader2, HardHat, Search } from "lucide-react";
import { SubStatusBadge } from "./SubStatusBadge";

interface Sub {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
  specialties: string[];
  status: string;
  avg_rating: number;
  default_rate: number | null;
  rate_type: string;
}

interface JobAssignSubDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  contactId: string | number;
}

export function JobAssignSubDialog({
  open,
  onClose,
  jobId,
  contactId,
}: JobAssignSubDialogProps) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [search, setSearch] = useState("");
  const [assigning, setAssigning] = useState<string | null>(null);
  const [roleDesc, setRoleDesc] = useState("");

  const { data: subs } = useGetList<Sub>("subcontractors", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "last_name", order: "ASC" },
    filter: { status: "active" },
  });

  const filtered = (subs || []).filter((s) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      s.first_name.toLowerCase().includes(q) ||
      s.last_name.toLowerCase().includes(q) ||
      s.company_name.toLowerCase().includes(q) ||
      s.specialties.some((sp: string) => sp.toLowerCase().includes(q))
    );
  });

  const handleAssign = useCallback(
    async (sub: Sub) => {
      setAssigning(sub.id);
      try {
        await create(
          "job_assignments",
          {
            data: {
              job_id: jobId,
              subcontractor_id: sub.id,
              role_description: roleDesc.trim(),
              rate: sub.default_rate,
              rate_type: sub.rate_type,
              status: "assigned",
            },
          },
          { returnPromise: true }
        );

        await create(
          "events",
          {
            data: {
              contact_id: contactId,
              job_id: jobId,
              event_type: "sub_assigned",
              payload: {
                subcontractor_id: sub.id,
                name: `${sub.first_name} ${sub.last_name}`.trim() || sub.company_name,
                role_description: roleDesc.trim(),
              },
            },
          },
          { returnPromise: true }
        );

        notify(`${sub.first_name || sub.company_name} assigned`, { type: "success" });
        refresh();
        onClose();
      } catch (err) {
        notify(`Failed: ${(err as Error).message}`, { type: "error" });
      } finally {
        setAssigning(null);
      }
    },
    [jobId, contactId, roleDesc, create, notify, refresh, onClose]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-md mx-4 shadow-xl max-h-[80vh] flex flex-col">
        <CardContent className="p-0 flex flex-col min-h-0">
          <div className="flex items-center justify-between p-4 border-b shrink-0">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <HardHat className="w-4 h-4" />
              Assign Subcontractor
            </h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-3 border-b shrink-0 space-y-2">
            <div className="flex items-center gap-2 px-2 py-1.5 border rounded">
              <Search className="w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search subs by name or specialty…"
                className="flex-1 text-sm outline-none"
              />
            </div>
            <input
              type="text"
              value={roleDesc}
              onChange={(e) => setRoleDesc(e.target.value)}
              placeholder="Role / scope (optional)"
              className="w-full px-3 py-1.5 border rounded text-sm"
            />
          </div>

          <div className="flex-1 overflow-y-auto divide-y">
            {filtered.length === 0 && (
              <p className="text-sm text-muted-foreground p-4 text-center">
                No matching subcontractors.
              </p>
            )}
            {filtered.map((sub) => {
              const name = `${sub.first_name} ${sub.last_name}`.trim() || sub.company_name;
              return (
                <div key={sub.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <SubStatusBadge status={sub.status} />
                      {sub.specialties.slice(0, 2).map((s: string) => (
                        <span key={s} className="text-xs bg-gray-100 text-gray-500 px-1 rounded">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleAssign(sub)}
                    disabled={assigning === sub.id}
                    className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 shrink-0"
                  >
                    {assigning === sub.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Plus className="w-3 h-3" />
                    )}
                    Assign
                  </button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
